/**
 * Integration Type Badge Component
 * Icon badge for displaying tool integration type
 */

import { cn } from "@utils/cn";
import type { IntegrationType } from "@/types/tools";

interface IntegrationTypeBadgeProps {
  type: IntegrationType;
  size?: "sm" | "md";
  showLabel?: boolean;
  className?: string;
}

// API icon
function ApiIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M7 7H5a2 2 0 00-2 2v8a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-2M7 7V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
    </svg>
  );
}

// SDK icon
function SdkIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6-1.6 1.6a1 1 0 101.4 1.4l2.3-2.3a1 1 0 000-1.4l-2.3-2.3a1 1 0 00-1.4 0zM9.3 6.3a1 1 0 00-1.4 0L5.6 8.6a1 1 0 000 1.4l2.3 2.3a1 1 0 001.4-1.4L7.7 9.3l1.6-1.6a1 1 0 000-1.4zM12 15a1 1 0 100 2 1 1 0 000-2z" />
    </svg>
  );
}

// CLI icon
function CliIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M4 17l6-6-6-6 1.41-1.41L12 10l-6.59 6.59L4 17zm8 0h8v-2h-8v2z" />
    </svg>
  );
}

// Standalone icon
function StandaloneIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zM4 18V8h16v10H4z" />
    </svg>
  );
}

// Browser extension icon
function ExtensionIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M20.5 11H19V7c0-1.1-.9-2-2-2h-4V3.5a2.5 2.5 0 00-5 0V5H4c-1.1 0-2 .9-2 2v3.8h1.5c1.5 0 2.7 1.2 2.7 2.7s-1.2 2.7-2.7 2.7H2V20c0 1.1.9 2 2 2h3.8v-1.5c0-1.5 1.2-2.7 2.7-2.7 1.5 0 2.7 1.2 2.7 2.7V22H17c1.1 0 2-.9 2-2v-4h1.5a2.5 2.5 0 000-5z" />
    </svg>
  );
}

// SaaS icon
function SaasIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M19.35 10.04A7.49 7.49 0 0012 4C9.11 4 6.6 5.64 5.35 8.04A5.994 5.994 0 000 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96z" />
    </svg>
  );
}

const integrationConfig: Record<
  IntegrationType,
  { label: string; icon: React.FC<{ className?: string }>; color: string }
> = {
  api: {
    label: "API",
    icon: ApiIcon,
    color: "text-blue-600 dark:text-blue-400",
  },
  sdk: {
    label: "SDK",
    icon: SdkIcon,
    color: "text-purple-600 dark:text-purple-400",
  },
  cli: {
    label: "CLI",
    icon: CliIcon,
    color: "text-green-600 dark:text-green-400",
  },
  standalone: {
    label: "Standalone",
    icon: StandaloneIcon,
    color: "text-gray-600 dark:text-gray-400",
  },
  browser_extension: {
    label: "Extension",
    icon: ExtensionIcon,
    color: "text-orange-600 dark:text-orange-400",
  },
  saas: {
    label: "SaaS",
    icon: SaasIcon,
    color: "text-cyan-600 dark:text-cyan-400",
  },
};

export function IntegrationTypeBadge({
  type,
  size = "sm",
  showLabel = true,
  className,
}: IntegrationTypeBadgeProps) {
  const config = integrationConfig[type];
  if (!config) return null;

  const Icon = config.icon;
  const iconSize = size === "sm" ? "w-3.5 h-3.5" : "w-4 h-4";
  const textSize = size === "sm" ? "text-xs" : "text-sm";

  return (
    <span
      className={cn("inline-flex items-center gap-1", config.color, className)}
      title={`Integration type: ${config.label}`}
    >
      <Icon className={iconSize} />
      {showLabel && (
        <span className={cn("font-medium", textSize)}>{config.label}</span>
      )}
    </span>
  );
}

/**
 * Get integration type description for tooltips
 */
// eslint-disable-next-line react-refresh/only-export-components
export function getIntegrationDescription(type: IntegrationType): string {
  switch (type) {
    case "api":
      return "REST or GraphQL API for integration";
    case "sdk":
      return "Software Development Kit available";
    case "cli":
      return "Command-line interface tool";
    case "standalone":
      return "Standalone application";
    case "browser_extension":
      return "Browser extension/add-on";
    case "saas":
      return "Software as a Service (web app)";
    default:
      return "";
  }
}
